<template>
  <v-row class="h-100 flex-md-nowrap" no-gutters data-cy="parent-details">
    <tree-navigation :key="item.id" :tree-id="item.id" :country-code="getMarket" />
    <v-col>
      <nv-page :header="item.name">
        <template slot="toolbar">
          <v-spacer />
          <nv-btn type="back" />
          <v-btn
            v-if="countryCode !== '*' && canViewImportRule()"
            color="accent"
            data-cy="parent-rules-import-btn"
            @click="bulkDomainsForm.show = true"
          >
            <v-icon class="mr-1">mdi-import</v-icon>
            <span>Rule Import</span>
          </v-btn>
          <nv-btn
            v-if="item.status != 'archived' && $auth.hasPermissions('owl.marketview')"
            type="edit"
            @click="openForm(item)"
          />
          <v-btn
            v-if="item.status != 'archived' && canView() && $auth.hasPermissions('owl.review')"
            color="accent"
            @click="handleMerge"
          >
            <v-icon class="mr-1">mdi-call-merge</v-icon>
            <span>Merge</span>
          </v-btn>
          <v-btn
            v-if="item.status == 'archived' && $auth.hasPermissions('owl')"
            color="#ff9800"
            @click="reactivateDialogue()"
          >
            <v-icon class="mr-1">mdi-update</v-icon>
            <span>Reactivate</span>
          </v-btn>
        </template>
        <v-card class="pa-4">
          <v-row dense>
            <nv-show cols="12" sm="2" header="Level">Parent</nv-show>
            <nv-show ref="name" cols="12" sm="4" header="Name">
              {{ item.name }}
              <mav-badge v-if="showMavBadge() && item.created_via_mav" />
            </nv-show>
            <nv-show cols="12" sm="2" header="Tree ID">{{ item.id }}</nv-show>
            <nv-show ref="status" cols="12" sm="4" header="Status">
              {{ item.status | humanize }}
            </nv-show>
            <nv-show cols="12" sm="2" header="Market">
              <CountryBadge :country="getMarket" />
            </nv-show>
            <nv-show ref="default-cid" cols="12" sm="4" header="Client IDs" no-placeholder>
              <cid-pane :parent-id="parentId" />
            </nv-show>
          </v-row>
        </v-card>
        <v-card class="mt-4 elevation-2">
          <v-tabs v-model="tabs" slider-color="accent">
            <v-tab data-cy="brands-tab">
              Brands
            </v-tab>
            <v-tab data-cy="localization-tab">
              Relabel
            </v-tab>
            <v-tab data-cy="history-tab">
              History
            </v-tab>
          </v-tabs>
          <v-tabs-items v-if="item.id != null" v-model="tabs">
            <v-tab-item data-cy="brands-pane">
              <brands-tab-list
                ref="brands-tab-list"
                :value="item.id"
                :can-add="item.status === 'active'"
                :country-code="getMarket"
              />
            </v-tab-item>
            <v-tab-item data-cy="localization-pane">
              <localization-tab-list :id="item.id" ref="localization-tab-list" type="parents" />
            </v-tab-item>
            <v-tab-item data-cy="history-pane">
              <proposals-history
                :id="parentId"
                ref="history-list"
                type="parents"
                :country-code="getMarket"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card>

        <parent-form v-model="form.show" :editing="form.edit" @saved="fetchData" />
        <merge-parent-form
          v-model="form.merge"
          :tree-id="item.id"
          :source-parent="item"
          :country-code="getMarket"
        />
        <bulk-csv-upload-form
          v-model="bulkDomainsForm.show"
          :tree-id="item.id"
          :localization="countryCode"
          rule-type="null"
          channel-type="parent"
        />
        <nv-confirm ref="confirm" />
      </nv-page>
    </v-col>
  </v-row>
</template>

<script>
import HTTP from '@/api/owl';
import BrandsTabList from '@/components/mv/parents/BrandsTabList.vue';
import LocalizationTabList from '@/components/mv/parents/LocalizationTabList';
import ProposalsHistory from '@/components/mv/ProposalsHistory';
import ParentForm from '@/components/mv/parents/ParentForm';
import BulkCsvUploadForm from '@/components/mv/BulkCSVUploadForm.vue';
import MergeParentForm from '@/components/mv/parents/MergeParent';
import TreeNavigation from '@/components/mv/TreeNavigation';
import CidPane from '@/components/mv/parents/CidPane';
import CountryBadge from '@/components/CountryBadge';
import MavBadge from '@/components/MavBadge';
import { getConfigMap } from '@/helpers/filters';

export default {
  components: {
    CidPane,
    BulkCsvUploadForm,
    TreeNavigation,
    ParentForm,
    MergeParentForm,
    BrandsTabList,
    LocalizationTabList,
    ProposalsHistory,
    CountryBadge,
    MavBadge,
  },
  data: () => ({
    item: {},
    tabs: 0,
    form: {
      show: false,
      edit: null,
      merge: false,
    },
    countryCode: '*',
    bulkDomainsForm: { show: false },
  }),
  computed: {
    parentId() {
      return parseInt(this.$route.params.parentId, 10);
    },
    getMarket() {
      return this.$route.query.countryCode == '*' ? 'Default' : this.$route.query.countryCode;
    },
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.countryCode = this.$route.query.countryCode;
  },
  methods: {
    canView() {
      const config = this.getConfigMap();
      return config['mergeParentFeature_owl_ui']?.enabled || false;
    },
    canViewImportRule() {
      const config = this.getConfigMap();
      return config['imporRulesFeature_owl_ui']?.enabled || false;
    },
    showMavBadge() {
      const config = this.getConfigMap();
      return config['mavFeature_owl_ui']?.enabled?.mav_badge === true;
    },
    fetchData() {
      let params = { country_code: this.$route.query.countryCode };
      HTTP.get(`/parents/${this.$route.params.parentId}`, { params }).then(res => {
        this.item = res.data;
      });
    },
    openForm(edit) {
      this.form.edit = edit;
      this.form.show = true;
    },
    handleMerge() {
      this.form.merge = true;
    },
    reactivateDialogue() {
      this.$refs.confirm
        .open(`You are trying to reactivate the parent "${this.item.name}"`)
        .then(() => {
          HTTP.put(`/admin/reactivate/parent`, { id: this.item.id })
            .then(() => {
              this.$flash('success', 'Successful');
            })
            .catch(err => {
              this.$flash('error', err.toString());
            });
        });
    },
    getConfigMap,
  },
};
</script>
